import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Milk, Plus, Edit, Trash2, Check, X } from 'lucide-react';
import { getMilkBrands, createMilkBrand, updateMilkBrand, deleteMilkBrand } from '../../api';

const MilkBrandManagement = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', defaultOpenDuration: 4 });
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({ name: '', defaultOpenDuration: 4 });

  const fetchBrands = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getMilkBrands();
      setBrands(response.data);
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des marques');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBrands();
  }, [fetchBrands]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    try {
      setLoading(true);
      setError(null);
      await createMilkBrand({
        name: formData.name.trim(),
        defaultOpenDuration: parseInt(formData.defaultOpenDuration) || 4,
      });
      setFormData({ name: '', defaultOpenDuration: 4 });
      setShowForm(false);
      fetchBrands();
    } catch (err) {
      if (err.response?.status === 409) {
        setError('Cette marque existe déjà');
      } else {
        setError("Erreur lors de l'ajout");
        console.error(err);
      }
      setLoading(false);
    }
  };

  const startEdit = (brand) => {
    setEditId(brand.id);
    setEditData({ name: brand.name, defaultOpenDuration: brand.defaultOpenDuration ?? 4 });
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditData({ name: '', defaultOpenDuration: 4 });
  };

  const saveEdit = async (id) => {
    if (!editData.name.trim()) { cancelEdit(); return; }
    try {
      setLoading(true);
      setError(null);
      await updateMilkBrand(id, {
        name: editData.name.trim(),
        defaultOpenDuration: parseInt(editData.defaultOpenDuration) || 4,
      });
      cancelEdit();
      fetchBrands();
    } catch (err) {
      if (err.response?.status === 409) {
        setError('Ce nom existe déjà');
      } else {
        setError('Erreur lors de la modification');
        console.error(err);
      }
      setLoading(false);
    }
  };

  const handleDelete = async (brand) => {
    const count = brand._count?.batches || 0;
    if (count > 0) {
      setError(`Impossible de supprimer "${brand.name}" : ${count} lot(s) lié(s).`);
      return;
    }
    if (!window.confirm(`Supprimer la marque "${brand.name}" ?`)) return;
    try {
      setLoading(true);
      setError(null);
      await deleteMilkBrand(brand.id);
      fetchBrands();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
      setLoading(false);
    }
  };

  if (loading && brands.length === 0) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <Card><CardContent><p className="text-gray-500 py-8 text-center">Chargement...</p></CardContent></Card>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Milk size={20} />
            Marques de lait
          </CardTitle>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4">{error}</div>
          )}

          {showForm && (
            <form onSubmit={handleAdd} className="mb-6 p-4 bg-gray-50 rounded-lg space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  type="text"
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  value={formData.name}
                  onChange={(e) => setFormData(f => ({ ...f, name: e.target.value }))}
                  placeholder="Nom de la marque * (ex: Gallia, Guigoz)"
                  required
                  autoFocus
                />
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="1"
                    className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    value={formData.defaultOpenDuration}
                    onChange={(e) => setFormData(f => ({ ...f, defaultOpenDuration: e.target.value }))}
                  />
                  <span className="text-sm text-gray-600">semaines après ouverture (défaut)</span>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition disabled:opacity-50"
                  disabled={loading || !formData.name.trim()}
                >
                  <Check size={18} />
                  Créer
                </button>
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setFormData({ name: '', defaultOpenDuration: 4 }); }}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-md transition"
                >
                  Annuler
                </button>
              </div>
              <p className="text-xs text-gray-500">
                Les marques configurées ici seront proposées lors de l'enregistrement d'un lot de lait.
              </p>
            </form>
          )}

          <div>
            <h3 className="font-semibold text-gray-700 mb-3">Marques ({brands.length})</h3>
            {brands.length === 0 ? (
              <p className="text-gray-500 text-sm py-4 text-center">Aucune marque. Ajoutez-en une ci-dessus.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left px-3 py-2 text-sm font-semibold text-gray-700">Marque</th>
                      <th className="text-center px-3 py-2 text-sm font-semibold text-gray-700">Durée après ouverture</th>
                      <th className="text-center px-3 py-2 text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {brands.map((brand) => (
                      <tr key={brand.id} className="border-t hover:bg-gray-50 transition">
                        <td className="px-3 py-2">
                          {editId === brand.id ? (
                            <input
                              type="text"
                              className="w-full px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                              value={editData.name}
                              onChange={(e) => setEditData(d => ({ ...d, name: e.target.value }))}
                              onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(brand.id); if (e.key === 'Escape') cancelEdit(); }}
                              autoFocus
                            />
                          ) : (
                            <span className="text-gray-800 font-medium">{brand.name}</span>
                          )}
                        </td>
                        <td className="text-center px-3 py-2">
                          {editId === brand.id ? (
                            <div className="flex items-center justify-center gap-2">
                              <input
                                type="number"
                                min="1"
                                className="w-20 px-2 py-1 border border-gray-300 rounded-md text-sm text-center"
                                value={editData.defaultOpenDuration}
                                onChange={(e) => setEditData(d => ({ ...d, defaultOpenDuration: e.target.value }))}
                              />
                              <span className="text-xs text-gray-500">sem.</span>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-600">
                              {brand.defaultOpenDuration} semaine{brand.defaultOpenDuration > 1 ? 's' : ''}
                            </span>
                          )}
                        </td>
                        <td className="text-center px-3 py-2">
                          <div className="flex justify-center gap-1">
                            {editId === brand.id ? (
                              <>
                                <button onClick={() => saveEdit(brand.id)} className="p-1 text-green-600 hover:bg-green-50 rounded-full transition" title="Enregistrer">
                                  <Check size={16} />
                                </button>
                                <button onClick={cancelEdit} className="p-1 text-gray-500 hover:bg-gray-200 rounded-full transition" title="Annuler">
                                  <X size={16} />
                                </button>
                              </>
                            ) : (
                              <>
                                <button onClick={() => startEdit(brand)} className="p-1 text-primary hover:bg-primary/5 rounded-full transition" title="Modifier" disabled={loading}>
                                  <Edit size={16} />
                                </button>
                                <button onClick={() => handleDelete(brand)} className="p-1 text-red-600 hover:bg-red-50 rounded-full transition" title="Supprimer" disabled={loading}>
                                  <Trash2 size={16} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      {!showForm && (
        <button
          onClick={() => setShowForm(true)}
          className="fixed bottom-6 right-6 z-50 bg-primary text-white rounded-full p-4 shadow-lg hover:bg-primary-dark transition-all hover:scale-105"
          title="Ajouter une marque"
        >
          <Plus size={24} />
        </button>
      )}
    </div>
  );
};

export default MilkBrandManagement;
